import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, MapPin, Phone, Mail, ChevronDown,
  Award, CheckCircle2, Factory, Truck, Star,
  Droplets, Layers, Mountain, Globe2, ArrowLeft
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/* ─── Navigation ───────────────────────────────────────────────── */
const navLinks = [
  { label: "الرئيسية", href: "#hero" },
  { label: "عن الشركة", href: "#about" },
  { label: "منتجاتنا", href: "#products" },
  { label: "مواقعنا", href: "#locations" },
  { label: "الجودة", href: "#quality" },
  { label: "تواصل معنا", href: "#contact" },
];

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(typeof window !== "undefined" && window.scrollY > 50);
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handler);
      return () => window.removeEventListener("scroll", handler);
    }
  }, []);

  const scrollTo = (href: string) => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 35 }}
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-card/95 shadow-lg border-b border-border/50" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground leading-tight">لؤلؤة سيناء</p>
            <p className="text-xs text-muted-foreground leading-tight">العاصمة جروب</p>
          </div>
        </div>

        {/* Desktop Links */}
        <ul className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <li key={link.label}>
              <button
                onClick={() => scrollTo(link.href)}
                className="text-sm text-foreground/80 hover:text-primary transition-colors duration-200 font-medium"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <Button
          onClick={() => scrollTo("#contact")}
          className="hidden lg:flex bg-primary hover:bg-primary/90 text-white"
          size="sm"
        >
          طلب عرض سعر
        </Button>

        {/* Mobile toggle */}
        <button onClick={() => setOpen(!open)} className="lg:hidden text-foreground">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card border-t border-border"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="text-right text-base text-foreground py-2 hover:text-primary transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => scrollTo("#contact")}
                className="bg-primary text-white mt-2"
              >
                طلب عرض سعر
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

/* ─── Hero ──────────────────────────────────────────────────────── */
function Hero() {
  const scrollTo = (href: string) => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, oklch(0.18 0.04 240) 0%, oklch(0.22 0.06 210) 50%, oklch(0.15 0.03 195) 100%)",
      }}
    >
      {/* Decorative circles */}
      <div
        className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, var(--accent) 0%, transparent 70%)" }}
      />
      {/* Animated salt crystals (CSS shapes) */}
      {[...Array(18)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-sm opacity-20 pointer-events-none"
          style={{
            width: `${6 + (i % 5) * 4}px`,
            height: `${6 + (i % 5) * 4}px`,
            left: `${(i * 5.5) % 100}%`,
            top: `${(i * 11 + 10) % 90}%`,
            background: "white",
            rotate: `${i * 20}deg`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 3 + (i % 4),
            repeat: Infinity,
            delay: i * 0.3,
            ease: "easeInOut",
          }}
        />
      ))}

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 text-sm px-4 py-1.5">
              العاصمة جروب • منذ عقود من الخبرة
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6"
          >
            لؤلؤة سيناء{" "}
            <span style={{ color: "var(--primary)" }}>للملح</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="text-xl text-white/80 leading-relaxed mb-8 max-w-2xl"
          >
            الرائد في إنتاج وتكرير وتعبئة الملح في جمهورية مصر العربية.
            جودة لا تُنافَس، مواصفات عالمية، وخبرة متراكمة في خدمة السوقين المحلي والدولي.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-wrap gap-4"
          >
            <Button
              onClick={() => scrollTo("#products")}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white text-base px-8 py-6"
            >
              تعرف على منتجاتنا
            </Button>
            <Button
              onClick={() => scrollTo("#contact")}
              variant="outline"
              size="lg"
              className="border-white/40 text-white hover:bg-white/10 text-base px-8 py-6"
            >
              تواصل معنا
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="flex flex-wrap gap-8 mt-14"
          >
            {[
              { value: "٤+", label: "مواقع إنتاجية" },
              { value: "٣", label: "خطوط منتجات" },
              { value: "١٠٠٪", label: "مطابقة للمواصفات" },
              { value: "تصدير", label: "لأسواق عالمية" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl font-bold text-white font-mono">{stat.value}</p>
                <p className="text-sm text-white/60 mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("#about")}
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors"
      >
        <ChevronDown size={32} />
      </motion.button>
    </section>
  );
}

/* ─── About ─────────────────────────────────────────────────────── */
function About() {
  const features = [
    {
      icon: <Award className="w-6 h-6" />,
      title: "معايير دولية",
      desc: "مطابقة كاملة للمواصفات القياسية المصرية والعالمية",
    },
    {
      icon: <Factory className="w-6 h-6" />,
      title: "بنية تحتية متطورة",
      desc: "مصانع حديثة موزعة استراتيجياً على خريطة مصر",
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "سلسلة توريد متكاملة",
      desc: "من الملاحات حتى التسليم للعميل بكفاءة عالية",
    },
    {
      icon: <Globe2 className="w-6 h-6" />,
      title: "حضور دولي",
      desc: "تصدير إلى أسواق عالمية بمواصفات تلبي التوقعات",
    },
  ];

  return (
    <section id="about" className="py-28 bg-background">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, type: "spring", stiffness: 200, damping: 30 }}
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              عن الشركة
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-6">
              رواد صناعة الملح
              <br />
              <span className="text-primary">في مصر والمنطقة</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              شركة لؤلؤة سيناء للملح، إحدى الشركات المتميزة في محفظة <strong className="text-foreground">العاصمة جروب</strong>،
              تجمع بين الخبرة الطويلة في استخراج الملح من ملاحات سيناء وسيوة وبين أحدث التقنيات الصناعية
              في التكرير والتعبئة والتصنيع.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              نُقدّم ثلاثة خطوط منتجات متكاملة تخدم الاستهلاك الآدمي، والقطاع الصناعي، والتصدير —
              مع التزام راسخ بمعايير الجودة والاستدامة.
            </p>
          </motion.div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.1,
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
                whileHover={{ scale: 1.02 }}
                className="bg-card border border-border rounded-2xl p-6"
                style={{
                  boxShadow: "0 4px 20px -4px color-mix(in srgb, var(--primary) 10%, transparent)",
                }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {f.icon}
                </div>
                <h3 className="font-bold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Products ──────────────────────────────────────────────────── */
const products = [
  {
    icon: <Droplets className="w-8 h-8" />,
    name: "الملح المكرر",
    subtitle: "عالي النقاء",
    color: "from-cyan-600 to-blue-700",
    badges: ["للاستهلاك الآدمي", "مطابق للمواصفات المصرية", "مواصفات دولية"],
    desc: "ملح مكرر بأعلى معايير النقاء، مناسب للاستخدام الغذائي اليومي، يُنتج وفق المواصفات القياسية المصرية والمتطلبات الدولية.",
    details: ["نقاء ≥ 99.5%", "خالٍ من الشوائب", "مُغلق تعبئة صحية", "متوفر بأحجام متعددة"],
  },
  {
    icon: <Layers className="w-8 h-8" />,
    name: "الملح المغسول الخشن",
    subtitle: "للاستخدامات المتعددة",
    color: "from-teal-600 to-emerald-700",
    badges: ["صناعي", "أعلاف حيوانية", "معالجة مياه"],
    desc: "ملح مغسول خشن الحبيبات، مثالي للاستخدامات الصناعية ومعالجة المياه وتغذية الأعلاف، بمعايير نقاء عالية.",
    details: ["حبيبات خشنة منتظمة", "خلو من الطين والشوائب", "مناسب لمحطات تحلية المياه", "كميات ضخمة للصناعة"],
  },
  {
    icon: <Mountain className="w-8 h-8" />,
    name: "الملح الصخري الخام",
    subtitle: "من ملاحات سيناء وسيوة",
    color: "from-indigo-600 to-violet-700",
    badges: ["للتصدير", "صناعي", "خام طبيعي"],
    desc: "ملح صخري خام مستخرج من ملاحات سيناء وسيوة بأعلى درجات النقاء الطبيعي وأقل نسبة رطوبة — مثالي للتصدير.",
    details: ["رطوبة منخفضة جداً", "نقاء طبيعي عالي", "مناسب للصناعات الكيميائية", "يُصدَّر بكميات كبيرة"],
  },
];

function Products() {
  return (
    <section id="products" className="py-28" style={{ background: "oklch(0.97 0.005 200)" }}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">منتجاتنا</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            ثلاثة خطوط إنتاج متكاملة
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            نقدم منتجات الملح بجودة متميزة لتلبية احتياجات السوق المحلي والعالمي
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {products.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: i * 0.15,
                type: "spring",
                stiffness: 260,
                damping: 28,
              }}
              whileHover={{ scale: 1.02 }}
              className="bg-card border border-border rounded-3xl overflow-hidden"
              style={{
                boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)",
              }}
            >
              {/* Header gradient */}
              <div className={`bg-gradient-to-br ${p.color} p-8 flex flex-col gap-3`}>
                <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-white">
                  {p.icon}
                </div>
                <div>
                  <p className="text-white/70 text-sm">{p.subtitle}</p>
                  <h3 className="text-2xl font-bold text-white">{p.name}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {p.badges.map((b) => (
                    <Badge key={b} className="bg-white/20 text-white border-0 text-xs">
                      {b}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{p.desc}</p>
                <ul className="space-y-2">
                  {p.details.map((d) => (
                    <li key={d} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Locations ─────────────────────────────────────────────────── */
const locations = [
  {
    name: "مصانع شمال سيناء",
    icon: "🏭",
    products: "الملح المكرر والملح المغسول الخشن",
    desc: "مصانع حديثة لإنتاج الملح المكرر وخطوط غسيل الملح الخشن بطاقة إنتاجية عالية.",
    highlight: "إنتاج رئيسي",
  },
  {
    name: "مصنع جمصة",
    icon: "⚙️",
    products: "تابع لمجموعة العاصمة جروب",
    desc: "منشأة متطورة تُعدّ من المشروعات الاستراتيجية في محفظة مجموعة العاصمة جروب.",
    highlight: "العاصمة جروب",
  },
  {
    name: "مصنع البحيرة والإسكندرية",
    icon: "🚢",
    products: "التصدير والأسواق الدولية",
    desc: "موقع استراتيجي بالقرب من الموانئ يُيسّر عمليات التصدير بكفاءة لوجستية عالية.",
    highlight: "بوابة التصدير",
  },
  {
    name: "ملاحات سيوة",
    icon: "🏔️",
    products: "الملح الصخري الخام",
    desc: "ملاحات طبيعية تنتج الملح الصخري الخام بأعلى درجات النقاء وأقل معدلات الرطوبة.",
    highlight: "خام طبيعي",
  },
];

function Locations() {
  return (
    <section id="locations" className="py-28 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">مواقعنا</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            شبكة إنتاج موزعة
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            مصانع وملاحات في مواقع استراتيجية على خريطة مصر تضمن قدرتنا على التوريد المستمر
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {locations.map((loc, i) => (
            <motion.div
              key={loc.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.5,
                delay: i * 0.1,
                type: "spring",
                stiffness: 280,
                damping: 30,
              }}
              whileHover={{ scale: 1.03 }}
              className="bg-card border border-border rounded-2xl p-6 relative overflow-hidden"
              style={{
                boxShadow: "0 4px 20px -4px color-mix(in srgb, var(--primary) 12%, transparent)",
              }}
            >
              <Badge className="mb-4 bg-primary/10 text-primary border-0 text-xs">
                {loc.highlight}
              </Badge>
              <div className="text-4xl mb-3">{loc.icon}</div>
              <h3 className="font-bold text-foreground text-lg mb-1">{loc.name}</h3>
              <p className="text-xs text-primary font-medium mb-3">{loc.products}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{loc.desc}</p>

              <div
                className="absolute bottom-0 right-0 w-20 h-20 rounded-full opacity-5 pointer-events-none"
                style={{ background: "var(--primary)", transform: "translate(30%, 30%)" }}
              />
            </motion.div>
          ))}
        </div>

        {/* Map placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-12 rounded-3xl overflow-hidden border border-border"
          style={{
            background: "linear-gradient(135deg, oklch(0.16 0.04 220) 0%, oklch(0.20 0.06 200) 100%)",
            minHeight: "280px",
          }}
        >
          <div className="flex flex-col items-center justify-center h-full py-16 text-center">
            <MapPin className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">موقعنا الجغرافي</h3>
            <p className="text-white/60 max-w-md">
              مصانعنا تمتد من سيناء في الشرق إلى الإسكندرية في الشمال الغربي، مروراً بجمصة وسيوة
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {["شمال سيناء", "جمصة", "البحيرة", "الإسكندرية", "سيوة"].map((city) => (
                <Badge
                  key={city}
                  className="bg-white/10 text-white border-white/20"
                >
                  <MapPin className="w-3 h-3 ml-1" />
                  {city}
                </Badge>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Quality ───────────────────────────────────────────────────── */
const qualityItems = [
  {
    icon: <Star className="w-6 h-6" />,
    title: "مواصفات قياسية مصرية",
    desc: "كافة منتجاتنا الغذائية مطابقة للمواصفات القياسية المصرية للملح الطعام.",
  },
  {
    icon: <Globe2 className="w-6 h-6" />,
    title: "معايير دولية",
    desc: "نلتزم بالمتطلبات الدولية للجودة لضمان وصول منتجاتنا لأسواق التصدير.",
  },
  {
    icon: <CheckCircle2 className="w-6 h-6" />,
    title: "رقابة جودة متواصلة",
    desc: "مختبرات داخلية وخارجية تُراقب جودة الإنتاج في كل مراحل التصنيع.",
  },
  {
    icon: <Award className="w-6 h-6" />,
    title: "شهادات الجودة",
    desc: "نحمل شهادات اعتماد معتمدة تعكس مستوى منتجاتنا وخدماتنا.",
  },
];

function Quality() {
  return (
    <section
      id="quality"
      className="py-28"
      style={{
        background: "linear-gradient(160deg, oklch(0.96 0.01 200) 0%, oklch(0.94 0.02 195) 100%)",
      }}
    >
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {qualityItems.map((q, i) => (
              <motion.div
                key={q.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1, type: "spring", stiffness: 300, damping: 30 }}
                className="bg-background border border-border rounded-2xl p-6"
                style={{
                  boxShadow: "0 4px 24px -6px color-mix(in srgb, var(--primary) 12%, transparent)",
                }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {q.icon}
                </div>
                <h3 className="font-bold text-foreground mb-2">{q.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{q.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, type: "spring", stiffness: 200, damping: 30 }}
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">الجودة والاعتماد</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-6">
              التزامنا بالجودة
              <br />
              <span className="text-primary">قيمة لا تتغير</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              منذ نشأتنا ونحن نضع معايير الجودة في صدارة أولوياتنا. كل كيلوغرام من ملحنا يمر
              بعمليات رقابة صارمة في مختبراتنا المعتمدة قبل وصوله إلى العميل.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              هدفنا دائماً هو تجاوز توقعات عملائنا، سواء في السوق المحلي أو في أسواق التصدير الدولية.
            </p>

            {/* Progress bars */}
            <div className="mt-8 space-y-4">
              {[
                { label: "نقاء الملح المكرر", value: 99 },
                { label: "رضا العملاء", value: 97 },
                { label: "مطابقة المواصفات", value: 100 },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground font-medium">{item.label}</span>
                    <span className="text-primary font-mono font-bold">{item.value}٪</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.value}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── Contact ───────────────────────────────────────────────────── */
function Contact() {
  return (
    <section id="contact" className="py-28 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">تواصل معنا</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            نسعد بخدمتكم
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            للاستفسارات التجارية وطلبات عروض الأسعار، تواصل معنا وسيُسعدنا الرد في أقرب وقت
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, type: "spring", stiffness: 200, damping: 30 }}
            className="space-y-6"
          >
            {[
              {
                icon: <Phone className="w-5 h-5" />,
                label: "الهاتف",
                value: "للتواصل والاستفسارات التجارية",
              },
              {
                icon: <Mail className="w-5 h-5" />,
                label: "البريد الإلكتروني",
                value: "info@loaloasinai.com",
              },
              {
                icon: <MapPin className="w-5 h-5" />,
                label: "المقر الرئيسي",
                value: "جمهورية مصر العربية",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                  <p className="font-medium text-foreground">{item.value}</p>
                </div>
              </div>
            ))}

            {/* About group */}
            <div
              className="rounded-2xl p-6"
              style={{
                background: "linear-gradient(135deg, oklch(0.18 0.04 220), oklch(0.22 0.06 200))",
              }}
            >
              <h3 className="text-white font-bold text-lg mb-2">العاصمة جروب</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                لؤلؤة سيناء للملح هي إحدى الشركات الرائدة ضمن محفظة مجموعة العاصمة جروب
                المتخصصة في الاستثمارات الصناعية والتجارية في مصر.
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, type: "spring", stiffness: 200, damping: 30 }}
            className="bg-card border border-border rounded-3xl p-8"
            style={{
              boxShadow: "0 8px 40px -8px color-mix(in srgb, var(--primary) 15%, transparent)",
            }}
          >
            <h3 className="text-xl font-bold text-foreground mb-6">أرسل استفساراً</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">الاسم</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="اسمك الكريم"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">الشركة</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="اسم شركتك"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1.5">البريد الإلكتروني</label>
                <input
                  type="email"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="email@company.com"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1.5">المنتج المطلوب</label>
                <select className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                  <option value="">اختر المنتج</option>
                  <option>ملح مكرر عالي النقاء</option>
                  <option>ملح مغسول خشن</option>
                  <option>ملح صخري خام</option>
                  <option>أكثر من منتج</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1.5">رسالتك</label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  placeholder="تفاصيل احتياجاتك أو استفسارك..."
                />
              </div>
              <Button className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-base">
                إرسال الاستفسار
                <ArrowLeft className="w-4 h-4 mr-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer
      className="py-12 border-t border-white/10"
      style={{
        background: "linear-gradient(135deg, oklch(0.15 0.04 220) 0%, oklch(0.18 0.05 200) 100%)",
      }}
    >
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-10 mb-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <Droplets className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold">لؤلؤة سيناء للملح</p>
                <p className="text-white/50 text-xs">العاصمة جروب</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              الرائد في إنتاج وتكرير وتعبئة الملح في مصر، نلتزم بأعلى معايير الجودة لخدمة
              السوقين المحلي والعالمي.
            </p>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-bold mb-4">منتجاتنا</h4>
            <ul className="space-y-2">
              {["الملح المكرر عالي النقاء", "الملح المغسول الخشن", "الملح الصخري الخام"].map((p) => (
                <li key={p} className="text-white/60 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-white font-bold mb-4">مواقعنا</h4>
            <ul className="space-y-2">
              {["مصانع شمال سيناء", "مصنع جمصة", "مصنع البحيرة والإسكندرية", "ملاحات سيوة"].map((loc) => (
                <li key={loc} className="text-white/60 text-sm flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
                  {loc}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} لؤلؤة سيناء للملح — العاصمة جروب. جميع الحقوق محفوظة.
          </p>
          <p className="text-white/30 text-xs">
            إنتاج مصري بجودة عالمية
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ─── Page ──────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <div className="min-h-screen bg-background font-sans" style={{ fontFamily: "Cairo, 'IBM Plex Sans Arabic', sans-serif" }}>
      <Navbar />
      <Hero />
      <About />
      <Products />
      <Locations />
      <Quality />
      <Contact />
      <Footer />
    </div>
  );
}
